import { orderedPropertyEntries } from './registrySchemaUtils';
import { formatEntryValue } from './registryAttributes';

/** Estensioni accettate dal selettore file dell'import. */
export const ACCEPT_IMPORT = '.csv,.xlsx';

/** Esiti di una riga d'import: etichetta plurale (conteggi), singolare (riga) e colore del chip. */
export const IMPORT_OUTCOMES = {
  INSERT: { label: 'nuove', one: 'nuova', single: 'Nuova', color: 'success' },
  UPDATE: { label: 'aggiornate', one: 'aggiornata', single: 'Aggiornata', color: 'info' },
  UNCHANGED: { label: 'invariate', one: 'invariata', single: 'Invariata', color: 'default' },
  INVALID: { label: 'non valide', one: 'non valida', single: 'Non valida', color: 'error' },
};

const COUNT_FIELDS = [
  ['INSERT', 'toInsert'],
  ['UPDATE', 'toUpdate'],
  ['UNCHANGED', 'unchanged'],
  ['INVALID', 'invalid'],
];

/** Conteggi dell'anteprima per esito, nell'ordine INSERT, UPDATE, UNCHANGED, INVALID. */
export const outcomeCounts = (preview) =>
  COUNT_FIELDS.map(([outcome, field]) => {
    const count = (preview && preview[field]) || 0;
    return {
      outcome,
      count,
      label: count === 1 ? IMPORT_OUTCOMES[outcome].one : IMPORT_OUTCOMES[outcome].label,
      color: IMPORT_OUTCOMES[outcome].color,
    };
  });

/** Righe che l'import scriverebbe davvero: nuove piu' aggiornate. */
export const writableCount = (preview) =>
  ((preview && preview.toInsert) || 0) + ((preview && preview.toUpdate) || 0);

export const importButtonLabel = (preview) => {
  const n = writableCount(preview);
  if (n === 0) return 'Niente da importare';
  return n === 1 ? 'Importa 1 voce' : `Importa ${n} voci`;
};

/**
 * Esito dell'import: «Importate N voci: X nuove, Y aggiornate. Z invariate, W saltate.»
 * I segmenti a zero sono omessi, tranne il totale.
 */
export const resultText = (result) => {
  const { inserted = 0, updated = 0, unchanged = 0, skipped = 0 } = result || {};
  const segment = (n, singular, plural) => (n ? `${n} ${n === 1 ? singular : plural}` : null);
  const written = [
    segment(inserted, 'nuova', 'nuove'),
    segment(updated, 'aggiornata', 'aggiornate'),
  ].filter(Boolean);
  const others = [
    segment(unchanged, 'invariata', 'invariate'),
    segment(skipped, 'saltata', 'saltate'),
  ].filter(Boolean);
  const total = inserted + updated;
  let text = total === 1 ? 'Importata 1 voce' : `Importate ${total} voci`;
  text += written.length ? `: ${written.join(', ')}.` : '.';
  if (others.length) text += ` ${others.join(', ')}.`;
  return text;
};

// Valore normalizzato per il confronto: null/''/undefined -> null, il resto come stringa.
const normalize = (value) => (value === null || value === undefined || value === '' ? null : String(value));

// Valore da mostrare: formattato come nella tabella delle voci (booleani «Sì»/«No»), applicato al valore grezzo.
const shown = (def, raw) => {
  const text = formatEntryValue(def, raw);
  return text === '' ? null : text;
};

// Lo snapshot nullo arriva dall'API come {}: null, undefined e {} valgono «assente».
const snapshotOf = (snapshot) => snapshot || {};

/** Campi che differiscono fra due snapshot, nell'ordine dello schema: [{ field, label, before, after }]. */
export const entryChanges = (before, after, schema) => {
  const prev = snapshotOf(before);
  const next = snapshotOf(after);
  const known = orderedPropertyEntries(schema);
  const knownNames = new Set(known.map(([name]) => name));
  // Campi non piu' nello schema ma ancora presenti negli snapshot storici: in coda.
  const extra = [...new Set([...Object.keys(prev), ...Object.keys(next)])]
    .filter((name) => !knownNames.has(name))
    .map((name) => [name, null]);
  return [...known, ...extra]
    .map(([field, def]) => ({
      field,
      label: (def && def.name) || field,
      changed: normalize(prev[field]) !== normalize(next[field]),
      before: shown(def, prev[field]),
      after: shown(def, next[field]),
    }))
    .filter((change) => change.changed)
    .map(({ changed, ...change }) => change);
};

const valuedLines = (snapshot, schema) =>
  entryChanges({}, snapshot, schema).map((c) => `${c.label}: «${c.after}»`);

/** Righe di testo di una riga di storico: modifiche campo per campo, valori iniziali o valori eliminati. */
export const changeLines = (row, schema) => {
  switch (row && row.changeType) {
    case 'UPDATED':
      return entryChanges(row.attributesBefore, row.attributesAfter, schema).map(
        (c) => `${c.label}: ${c.before === null ? '—' : `«${c.before}»`} → ${c.after === null ? '—' : `«${c.after}»`}`,
      );
    case 'CREATED':
      return valuedLines(row.attributesAfter, schema);
    case 'DELETED':
      return ['voce eliminata', ...valuedLines(row.attributesBefore, schema)];
    default:
      return [];
  }
};
